import React from "react";

type ContainerSize = "large" | "small";

/**
 * Helper to get the sizes of a given widget container.
 * Ensure `createRef` used and not `useRef`, eg:
 * ```
 * const wrapperRef = React.createRef<HTMLDivElement>();
 * ```
 *
 * ### Example usage
 * ```
 * const { height, width, size } = useWidgetContainerSize(wrapperRef);
 * ```
 */
const useWidgetContainerSize = (containerRef: React.RefObject<HTMLDivElement>): { height: number, width: number, size: ContainerSize } => {
  const [ size, setSize ] = React.useState<ContainerSize>("small");
  const [ containerHeight, setContainerHeight ] = React.useState<number>(0);
  const [ containerWidth, setContainerWidth ] = React.useState<number>(0);

  // sets accessible size states based on the size of the widget so we can style on this instead of media queries
  React.useEffect(() => {
    if (containerRef.current) {
      const { width, height } = containerRef.current.getBoundingClientRect();

      setContainerHeight(height);
      setContainerWidth(width);

      if (width > 550) {
        setSize("large");
      } else {
        setSize("small");
      }
    }
  }, [ containerRef ]);

  return {
    size,
    height: containerHeight,
    width: containerWidth
  };
};

export default useWidgetContainerSize;